import { MarketingLayout } from "@/components/MarketingLayout";

export default function Privacy() {
  return (
    <MarketingLayout>
      <article className="mx-auto w-full max-w-3xl px-6 py-16 text-slate-700">
        <h1 className="text-3xl font-semibold tracking-tight text-slate-900 md:text-4xl">
          Privacy Policy
        </h1>
        <p className="mt-2 text-sm text-slate-500">Effective: April 30, 2026</p>

        <p className="mt-8 leading-relaxed">
          This Privacy Policy describes how Reputely (&ldquo;we&rdquo;,
          &ldquo;us&rdquo;) collects, uses, and shares information about you
          when you use reputely.co or related services (the
          &ldquo;Service&rdquo;).
        </p>

        <h2 className="mt-10 text-xl font-semibold text-slate-900">
          1. Information we collect
        </h2>
        <p className="mt-3 leading-relaxed">
          <strong className="text-slate-900">Information you provide:</strong>
        </p>
        <ul className="mt-2 list-disc pl-6 leading-relaxed space-y-1">
          <li>Email address you submit when requesting an audit;</li>
          <li>ASINs or Amazon storefront URLs you submit for analysis;</li>
          <li>
            Anything you send us by email, on a booking call, or through a
            support form.
          </li>
        </ul>
        <p className="mt-4 leading-relaxed">
          <strong className="text-slate-900">
            Information collected automatically:
          </strong>
        </p>
        <ul className="mt-2 list-disc pl-6 leading-relaxed space-y-1">
          <li>Pages you visit on reputely.co;</li>
          <li>
            Browser type, device, IP address, and similar technical metadata
            (collected by our hosting provider&rsquo;s standard server logs);
          </li>
          <li>
            Clickstream data through standard analytics, when enabled, in
            anonymized or aggregated form.
          </li>
        </ul>
        <p className="mt-4 leading-relaxed">
          <strong className="text-slate-900">
            Information from Amazon:
          </strong>{" "}
          Public reviews of the products (ASINs) you submit. We retrieve only
          publicly visible review content; we do not access your Amazon
          seller account, payment data, or buyer-private data.
        </p>

        <h2 className="mt-10 text-xl font-semibold text-slate-900">
          2. How we use information
        </h2>
        <ul className="mt-3 list-disc pl-6 leading-relaxed space-y-1">
          <li>
            To run the policy classifier on your reviews and identify
            potential violations;
          </li>
          <li>To email you the results of your audit;</li>
          <li>To respond to your questions and operate the Service;</li>
          <li>
            To improve our classifier accuracy (we may use anonymized
            classification examples to refine our prompts and models);
          </li>
          <li>
            To send you transactional notifications relevant to your audit
            (we do not send promotional or marketing email unless you ask).
          </li>
        </ul>

        <h2 className="mt-10 text-xl font-semibold text-slate-900">
          3. How we share information
        </h2>
        <p className="mt-3 leading-relaxed">
          We share information with service providers who help us operate the
          Service. Each operates under their own privacy practices.
        </p>
        <ul className="mt-3 list-disc pl-6 leading-relaxed space-y-1">
          <li>
            <strong className="text-slate-900">Amazon Web Services</strong> —
            hosting, database, email delivery, log storage.
          </li>
          <li>
            <strong className="text-slate-900">Anthropic</strong> — policy
            classification of review text.
          </li>
          <li>
            <strong className="text-slate-900">ImprovMX</strong> — inbound
            email forwarding for messages sent to @reputely.co addresses.
          </li>
          <li>
            <strong className="text-slate-900">Cal.com</strong> — scheduling
            for booking calls with us.
          </li>
          <li>
            <strong className="text-slate-900">Amazon.com</strong> — when we
            submit takedown requests on your behalf, we share the relevant
            review identifier, the cited policy clause, and our analysis.
          </li>
        </ul>
        <p className="mt-4 leading-relaxed">
          We do not sell your personal information. We may disclose
          information when required by law, to enforce our Terms, or to
          protect our rights.
        </p>

        <h2 className="mt-10 text-xl font-semibold text-slate-900">
          4. Processing of review data
        </h2>
        <p className="mt-3 leading-relaxed">
          Review text you submit (or that we retrieve from public Amazon
          listings on your behalf) is sent to Anthropic for policy
          classification. Anthropic processes the content under its privacy
          practices and, by default, does not use API content for training.
          Classifications, including the original review
          text, are stored in our database for your review and to refine
          our service.
        </p>

        <h2 className="mt-10 text-xl font-semibold text-slate-900">
          5. Data retention
        </h2>
        <p className="mt-3 leading-relaxed">
          We retain audit data and associated information for as long as
          needed to provide the Service and for a reasonable period after to
          satisfy legal, tax, or operational requirements. You may request
          deletion of your data at any time by emailing
          audits@reputely.co.
        </p>

        <h2 className="mt-10 text-xl font-semibold text-slate-900">
          6. Your rights
        </h2>
        <p className="mt-3 leading-relaxed">
          Depending on where you live, you may have the right to access,
          correct, delete, port, or restrict processing of your personal
          information, or to object to certain processing. To exercise these
          rights, email audits@reputely.co. We will respond within the time
          required by applicable law.
        </p>

        <h2 className="mt-10 text-xl font-semibold text-slate-900">
          7. California residents (CCPA / CPRA)
        </h2>
        <p className="mt-3 leading-relaxed">
          California residents have the right to know what personal
          information we collect, request its deletion, opt out of any sale
          or sharing for cross-context advertising (we do not sell or share
          personal information for cross-context advertising), and not be
          discriminated against for exercising these rights.
        </p>

        <h2 className="mt-10 text-xl font-semibold text-slate-900">
          8. EEA, UK, and Switzerland (GDPR / UK GDPR)
        </h2>
        <p className="mt-3 leading-relaxed">
          If you are in the EEA, UK, or Switzerland, our legal bases for
          processing your data are: (a) the performance of a contract (you
          requested an audit); (b) our legitimate interests in operating,
          securing, and improving the Service; and (c) compliance with legal
          obligations. You may lodge a complaint with your local supervisory
          authority.
        </p>

        <h2 className="mt-10 text-xl font-semibold text-slate-900">
          9. Children
        </h2>
        <p className="mt-3 leading-relaxed">
          The Service is not directed to children under 13 (or under 16 in
          the EEA). We do not knowingly collect personal information from
          children. If you believe a child has provided us information,
          contact us and we will delete it.
        </p>

        <h2 className="mt-10 text-xl font-semibold text-slate-900">
          10. Analytics and cookies
        </h2>
        <p className="mt-3 leading-relaxed">
          We use Google Tag Manager and Google Analytics 4 to understand how
          visitors use the Service so we can improve it. Analytics cookies are
          loaded only if you click <strong>Accept</strong> on the cookie banner
          shown on your first visit. If you decline, no analytics cookies are
          set and the GTM script is not loaded.
        </p>
        <p className="mt-3 leading-relaxed">
          You can change your choice at any time by clicking{" "}
          <strong>Cookie preferences</strong> in the footer of any page.
          Strictly necessary cookies that the Service needs to function
          (e.g., the admin session cookie) are always set and cannot be
          disabled.
        </p>

        <h2 className="mt-10 text-xl font-semibold text-slate-900">
          11. Security
        </h2>
        <p className="mt-3 leading-relaxed">
          We use industry-standard measures to protect data, including
          encryption in transit, infrastructure-level access controls, and
          minimal-privilege access for our team. No system is perfectly
          secure; we cannot guarantee absolute security.
        </p>

        <h2 className="mt-10 text-xl font-semibold text-slate-900">
          12. Changes to this policy
        </h2>
        <p className="mt-3 leading-relaxed">
          We may update this Privacy Policy. Material changes will be
          reflected in an updated &ldquo;Effective&rdquo; date. Continued use
          of the Service after changes constitutes acceptance.
        </p>

        <h2 className="mt-10 text-xl font-semibold text-slate-900">
          13. Contact
        </h2>
        <p className="mt-3 leading-relaxed">
          Questions about this policy or your data: audits@reputely.co.
        </p>
      </article>
    </MarketingLayout>
  );
}
